import React from 'react';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import Diagram from './Diagram';
import CodeTabs from './CodeTabs';
import './index.css';

// Inline formatting inside text: `code` and **bold**.
export const renderInline = (text) =>
  text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g).map((part, i) => {
    if (part.startsWith('`') && part.endsWith('`') && part.length > 1) {
      return <code key={i} className="inline-code">{part.slice(1, -1)}</code>;
    }
    if (part.startsWith('**') && part.endsWith('**') && part.length > 3) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });

const CodeBlock = ({ code, language, title, output }) => (
  <div className="code-block">
    {(title || language) && (
      <div className="code-block-bar">
        <span>{title || ''}</span>
        {language && <span className="code-block-lang">{language}</span>}
      </div>
    )}
    <pre className="article-code"><code>{code}</code></pre>
    {output && (
      <div className="code-output">
        <span className="code-output-label">Output</span>
        <pre><code>{output}</code></pre>
      </div>
    )}
  </div>
);

const calloutIcons = {
  tip: LightbulbIcon,
  note: InfoOutlinedIcon,
  warning: WarningAmberIcon
};

const calloutTitles = {
  tip: 'Tip',
  note: 'Note',
  warning: 'Watch out'
};

// Supported blocks: p, h2, h3, ul, ol, code, tip, callout, table, compare,
// qa, exercise, diagram, summary.
const renderBlock = (block, i) => {
  switch (block.type) {
    case 'h2':
      return <h2 key={i}>{block.text}</h2>;
    case 'h3':
      return <h3 key={i}>{block.text}</h3>;
    case 'ul':
      return <ul key={i}>{block.items.map((item, j) => <li key={j}>{renderInline(item)}</li>)}</ul>;
    case 'ol':
      return <ol key={i}>{block.items.map((item, j) => <li key={j}>{renderInline(item)}</li>)}</ol>;
    case 'code':
      // Multi-language samples (Java / Python / JavaScript) render as tabs
      return block.variants ? <CodeTabs key={i} {...block} /> : <CodeBlock key={i} {...block} />;
    case 'tip':
    case 'callout': {
      const variant = block.variant || 'tip';
      const Icon = calloutIcons[variant] || LightbulbIcon;
      return (
        <aside key={i} className={`article-callout callout-${variant}`}>
          <Icon sx={{ fontSize: 20 }} />
          <div>
            {block.type === 'callout' && <strong className="callout-title">{block.title || calloutTitles[variant]}</strong>}
            <p>{renderInline(block.text)}</p>
          </div>
        </aside>
      );
    }
    case 'table':
      return (
        <div key={i} className="article-table-wrap">
          <table className="article-table">
            <thead>
              <tr>{block.headers.map((h) => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r}>{row.map((cell, c) => <td key={c}>{renderInline(cell)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'compare':
      return (
        <div key={i} className="article-compare">
          <div className="compare-side compare-bad">
            <span className="compare-label"><HighlightOffIcon sx={{ fontSize: 18 }} /> {block.badLabel || 'Avoid'}</span>
            <pre className="article-code"><code>{block.bad}</code></pre>
          </div>
          <div className="compare-side compare-good">
            <span className="compare-label"><CheckCircleOutlineIcon sx={{ fontSize: 18 }} /> {block.goodLabel || 'Prefer'}</span>
            <pre className="article-code"><code>{block.good}</code></pre>
          </div>
          {block.text && <p className="compare-note">{renderInline(block.text)}</p>}
        </div>
      );
    case 'qa':
      return (
        <details key={i} className="article-qa">
          <summary>
            <QuestionAnswerOutlinedIcon sx={{ fontSize: 18 }} />
            <span>{renderInline(block.question)}</span>
          </summary>
          <div className="qa-answer">
            {[].concat(block.answer).map((para, j) => <p key={j}>{renderInline(para)}</p>)}
            {block.code && <CodeBlock code={block.code} language={block.language} output={block.output} />}
          </div>
        </details>
      );
    case 'exercise':
      return (
        <section key={i} className="article-exercise">
          <span className="exercise-label"><FitnessCenterIcon sx={{ fontSize: 18 }} /> Practice</span>
          <h3>{block.title}</h3>
          <p>{renderInline(block.task)}</p>
          {block.starter && <CodeBlock code={block.starter} language={block.language} title="Starter code" />}
          <details className="exercise-solution">
            <summary>Show solution</summary>
            <CodeBlock code={block.solution} language={block.language} output={block.output} />
            {block.explanation && <p>{renderInline(block.explanation)}</p>}
          </details>
        </section>
      );
    case 'diagram':
      return <Diagram key={i} {...block} />;
    case 'summary':
      return (
        <section key={i} className="article-summary">
          <h3>Key takeaways</h3>
          <ul>{block.items.map((item, j) => <li key={j}>{renderInline(item)}</li>)}</ul>
        </section>
      );
    default:
      return <p key={i}>{renderInline(block.text)}</p>;
  }
};

const ContentBlocks = ({ blocks }) => (
  <div className="article-body">
    {blocks.map(renderBlock)}
  </div>
);

export default ContentBlocks;
